import React, { useState, useEffect } from 'react';
import { ShieldAlert, RefreshCw, LogOut, Ban, AlertCircle, User, CheckCircle2, Circle, Send , ArrowRight } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../store/authSlice';
import Swal from 'sweetalert2';
import { authAPI, sellerAPI } from '../../api';
import './AccessModule.css';

interface AccessModuleProps {
  isBlock?: boolean;
  verification_status?: string;
}

export default function AccessModule({ isBlock = false, verification_status }: AccessModuleProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [seller, setSeller] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sellerAPI.getProfile().then(res => {
      const s = res.data.seller;
      setSeller(s);

    }).catch(() => {
    }).finally(() => {
      setLoading(false);
    });
  }, []);
  const isStoreInfoFilled = !!(
    // store info 
    seller?.storeinfo?.store_name &&
    seller?.storeinfo?.phone_number &&
    seller?.storeinfo?.owner_name &&
    seller?.storeinfo?.businessLicense?.length > 0 &&
    seller?.storeinfo?.panCard?.length > 0
  );
  const isAddressFilled = !!(
    seller?.address?.areaOrStreet &&
    seller?.address?.city &&
    seller?.address?.villageOrLocality &&
    seller?.address?.fullAddress &&
    seller?.temporaryLocation &&
    seller?.address?.state &&
    seller?.address?.postalCode
  );
  const isBankFilled = !!(
    seller?.bankdetails?.account_holder_name &&
    seller?.bankdetails?.account_number &&
    seller?.bankdetails?.ifsc_code &&
    seller?.bankdetails?.bank_name
  );

  useEffect(() => {
    if (verification_status === 'R') {
      const fetchReason = async () => {
        try {
          const userDetails = JSON.parse(localStorage.getItem('seller') || '{}');
          if (userDetails._id) {
            const res = await authAPI.getSellerStatus(userDetails._id);
            if (res.data && res.data.rejection_reason) {
              setRejectionReason(res.data.rejection_reason);
            }
          }
        } catch (err) {
          console.error('Failed to fetch rejection reason', err);
        }
      };
      fetchReason();
    }
  }, [verification_status]);

  // Automatically log out and redirect if blocked
  useEffect(() => {
    if (isBlock) {
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            localStorage.clear();
            dispatch(logout());
            navigate('/login');
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isBlock, dispatch, navigate]);

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="access-wrapper">
        <div className="glass-card fade-in access-card" style={{ minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <RefreshCw size={48} color="#14b8a6" className="spinner" />
        </div>
      </div>
    );
  }

  const HandleSendMessage = async () => {
    try {
      Swal.fire({
        title: 'Submitting...',
        text: 'Please wait while we submit your profile to the admin.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const res = await sellerAPI.submitVerification();

      if (res.data.response === 'S') {
        localStorage.removeItem('profileUpdatedAfterRejection');
        Swal.fire({
          icon: 'success',
          title: 'Submitted!',
          text: 'Your profile has been submitted to the admin for verification.',
          confirmButtonColor: '#3b82f6'
        }).then(() => {
          window.location.reload();
        });
      } else {
        Swal.fire('Error', res.data.message || 'Failed to submit profile', 'error');
      }
    } catch (error: any) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to submit profile', 'error');
    }
  };


  return (
    <div className="access-wrapper">
      <div className="glass-card fade-in access-card">
        {/* Pulsing Warning Container */}
        <div className={`access-icon-container ${isBlock ? 'access-icon-container--blocked' : 'access-icon-container--warning'}`}>
          {isBlock ? <Ban size={42} /> : <ShieldAlert size={42} />}
        </div>

        {/* Heading */}
        <h2 className="access-heading">
          {isBlock ? 'Your Account Was Blocked' : (verification_status === 'P' ?
            <>
              {(isStoreInfoFilled && isAddressFilled && isBankFilled) ? (
                'Your Account Is Under Verification'
              ) : (
                `You're almost there! Complete the remaining verification steps to full access to your account.`
              )}
            </>

            : 'Your Account Verification Rejected')}
        </h2>

        {/* Message */}
        <p className="access-message">
          {isBlock
            ? 'Your seller account has been blocked or suspended by the administrator. Please contact customer support for further information.'
            : verification_status === 'P'
              ? 'Your seller account has not been verified yet. Please wait until the administrator verifies your account.'
              : 'Your seller account has been rejected. Please contact the administrator for further information.'
          }
        </p>

        {verification_status === 'R' && rejectionReason && (
          <div className="access-rejection-box">
            <div className="access-rejection-icon">
              <AlertCircle size={22} />
            </div>
            <div className="access-rejection-content">
              <strong className="access-rejection-title">Required Changes & Reason</strong>
              <span className="access-rejection-desc">{rejectionReason}</span>
            </div>
          </div>
        )}

        {/* Progress Tracker */}
        {!isBlock && (
          <div className="access-progress-tracker" style={{ display: 'flex', justifyContent: 'center', gap: '32px', margin: '24px 0' }}>
            
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              onClick={() => navigate('/profile', { state: { activeTab: 'location' } })}
            >
              {isAddressFilled ? <CheckCircle2 size={32} color="#10b981" /> : <Circle size={32} color="#cbd5e1" />}
              <span style={{ fontSize: '13px', fontWeight: 600, color: isAddressFilled ? '#10b981' : '#64748b' }}>Location</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              onClick={() => navigate('/profile', { state: { activeTab: 'store' } })}
            >
              {isStoreInfoFilled ? <CheckCircle2 size={32} color="#10b981" /> : <Circle size={32} color="#cbd5e1" />}
              <span style={{ fontSize: '13px', fontWeight: 600, color: isStoreInfoFilled ? '#10b981' : '#64748b' }}>Store Information</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              onClick={() => navigate('/profile', { state: { activeTab: 'bank' } })}
            >
              {isBankFilled ? <CheckCircle2 size={32} color="#10b981" /> : <Circle size={32} color="#cbd5e1" />}
              <span style={{ fontSize: '13px', fontWeight: 600, color: isBankFilled ? '#10b981' : '#64748b' }}>Bank Details</span>
            </div>
          </div>
        )}

        {/* Actions */}
        {isBlock ? (
          <div className="access-blocked-actions">
            <div className="access-pulse-dot" />
            Redirecting to login page in {countdown} seconds...
          </div>
        ) : (
          <>

            {!(isStoreInfoFilled && isAddressFilled && isBankFilled) && (

              <div className="access-actions">
                <button
                  onClick={handleGoToProfile}
                  className="btn-primary access-btn"
                  style={(isStoreInfoFilled && isAddressFilled && isBankFilled) ? { background: '#3b82f6', borderColor: '#3b82f6' } : {}}
                >
                  <User size={18} /> Fill Profile
                </button>
              </div>
            )}
            {(verification_status === "R") && (isStoreInfoFilled && isAddressFilled && isBankFilled) && (

              <div className="access-actions">
                {localStorage.getItem('profileUpdatedAfterRejection') === 'true' ? (
                  <button
                    onClick={HandleSendMessage}
                    className="btn-primary access-btn"
                    style={(isStoreInfoFilled && isAddressFilled && isBankFilled) ? { background: '#0c4eb9ff', borderColor: '#3b82f6' } : {}}
                  >
                    <Send size={18} /> Re-Submit Profile
                  </button>
                ) : (
                  <button
                    onClick={handleGoToProfile}
                    className="btn-primary access-btn"
                    style={(isStoreInfoFilled && isAddressFilled && isBankFilled) ? { background: '#0c4eb9ff', borderColor: '#3b82f6' } : {}}
                  >
                    Go to Profile Section and Update Required Changes<ArrowRight size={18}/>
                  </button>
                )}
              </div>
            )}
            {(verification_status === "P") && (isStoreInfoFilled && isAddressFilled && isBankFilled) && !seller.submitted && (

              <div className="access-actions">
                <button
                  onClick={HandleSendMessage}
                  className="btn-primary access-btn"
                  style={(isStoreInfoFilled && isAddressFilled && isBankFilled) ? { background: '#0c4eb9ff', borderColor: '#3b82f6' } : {}}
                >
                  <Send size={18} /> Submit Profile
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
